import {
  capturePreservedInputFocus,
  restorePreservedInputFocus,
  type PreservedInputFocus,
} from "./preserveInputFocus";

const SCROLL_CONTAINER_SELECTOR = [
  ".recs-hscroll",
  ".sfe-income-month-selector",
  ".sfe-margin-cost-history-scroll",
  ".sfe-agents-run-log",
  ".sfe-advise-log-scroll",
].join(", ");

export interface PreservedPanelScroll {
  windowTop: number;
  windowLeft: number;
  containers: Array<{ index: number; top: number; left: number }>;
}

export interface PreservedPanelView {
  scroll: PreservedPanelScroll;
  focus: PreservedInputFocus | null;
  panelFocus: PreservedPanelFocus | null;
}

/** Non-ticker focus inside a panel (month-card editors, settings fields, etc.). */
export interface PreservedPanelFocus {
  selector: string;
  selectionStart: number | null;
  selectionEnd: number | null;
}

function scrollContainerNodes(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return [...root.querySelectorAll<HTMLElement>(SCROLL_CONTAINER_SELECTOR)];
}

export function capturePanelScroll(root: HTMLElement | null): PreservedPanelScroll {
  const containers = scrollContainerNodes(root);
  return {
    windowTop: window.scrollY,
    windowLeft: window.scrollX,
    containers: containers.map((el, index) => ({
      index,
      top: el.scrollTop,
      left: el.scrollLeft,
    })),
  };
}

export function restorePanelScroll(root: HTMLElement | null, captured: PreservedPanelScroll): void {
  if (Math.abs(window.scrollY - captured.windowTop) > 1) {
    window.scrollTo({ top: captured.windowTop, left: captured.windowLeft, behavior: "instant" });
  }
  const containers = scrollContainerNodes(root);
  for (const row of captured.containers) {
    const el = containers[row.index];
    if (!el) continue;
    if (Math.abs(el.scrollTop - row.top) > 1) el.scrollTop = row.top;
    if (Math.abs(el.scrollLeft - row.left) > 1) el.scrollLeft = row.left;
  }
}

function panelFocusSelector(el: Element): string | null {
  if (!(el instanceof HTMLElement)) return null;
  if (el.id) return `#${CSS.escape(el.id)}`;
  const monthDetail = el.closest(".sfe-income-month-detail");
  if (monthDetail instanceof HTMLElement) {
    const field = el.closest<HTMLElement>("[data-own-capital-field]");
    if (field?.dataset.ownCapitalField) {
      return `.sfe-income-month-detail[data-month="${CSS.escape(monthDetail.dataset.month || "")}"] [data-own-capital-field="${CSS.escape(field.dataset.ownCapitalField)}"] input, .sfe-income-month-detail[data-month="${CSS.escape(monthDetail.dataset.month || "")}"] [data-own-capital-field="${CSS.escape(field.dataset.ownCapitalField)}"] textarea`;
    }
    if (el.matches("input, textarea, select")) {
      const cls = [...el.classList].find((c) => c.startsWith("sfe-own-capital-") || c.startsWith("sfe-target-"));
      if (cls) {
        return `.sfe-income-month-detail[data-month="${CSS.escape(monthDetail.dataset.month || "")}"] .${CSS.escape(cls)}`;
      }
    }
  }
  const agentsPanel = el.closest(".sfe-agents-panel");
  if (agentsPanel && el.matches("input, textarea, select")) {
    const name = el.getAttribute("name");
    if (name) return `.sfe-agents-panel [name="${CSS.escape(name)}"]`;
    if (el.id) return `#${CSS.escape(el.id)}`;
    const dataKey = el.getAttribute("data-paper-interval-minutes") != null
      ? "[data-paper-interval-minutes]"
      : el.getAttribute("data-paper-top-n") != null
        ? "[data-paper-top-n]"
        : null;
    if (dataKey) return `.sfe-agents-panel ${dataKey}`;
  }
  return null;
}

export function capturePanelFocus(root: HTMLElement | null): PreservedPanelFocus | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !root?.contains(active)) return null;
  if (active instanceof HTMLInputElement && active.id && document.getElementById(active.id) === active) {
    return null; // handled by capturePreservedInputFocus
  }
  const selector = panelFocusSelector(active);
  if (!selector) return null;
  return {
    selector,
    selectionStart:
      active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement
        ? active.selectionStart
        : null,
    selectionEnd:
      active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement
        ? active.selectionEnd
        : null,
  };
}

export function restorePanelFocus(captured: PreservedPanelFocus | null, root: HTMLElement | null): void {
  if (!captured || !root) return;
  const el = root.querySelector<HTMLElement>(captured.selector);
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) {
    return;
  }
  el.focus({ preventScroll: true });
  if (
    (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) &&
    captured.selectionStart != null &&
    captured.selectionEnd != null &&
    typeof el.setSelectionRange === "function"
  ) {
    try {
      el.setSelectionRange(captured.selectionStart, captured.selectionEnd);
    } catch {
      /* ignore */
    }
  }
}

/** True when the user is typing or editing inside the panel — defer structural refreshes. */
export function isUserActiveInPanel(root: HTMLElement | null): boolean {
  if (!root) return false;
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !root.contains(active)) return false;
  if (active.matches("input, textarea, select, [contenteditable='true']")) return true;
  if (active.closest(".sfe-month-card-field-edit")) return true;
  if (active.closest(".sfe-content-dialog-overlay")) return true;
  return false;
}

export function capturePanelView(root: HTMLElement | null): PreservedPanelView {
  return {
    scroll: capturePanelScroll(root),
    focus: capturePreservedInputFocus(),
    panelFocus: capturePanelFocus(root),
  };
}

export function restorePanelView(root: HTMLElement | null, captured: PreservedPanelView): void {
  restorePanelScroll(root, captured.scroll);
  restorePreservedInputFocus(captured.focus);
  restorePanelFocus(captured.panelFocus, root);
}

export function withPreservedPanelView(root: HTMLElement | null, rerender: () => void): void {
  const captured = capturePanelView(root);
  rerender();
  requestAnimationFrame(() => restorePanelView(root, captured));
}
